import React from 'react';
import { bool } from 'prop-types';
import classNames from 'classnames';

import { LoadingPlaceholder } from '@thd-olt-component-react/loading-placeholder';
import { Image } from '@thd-olt-component-react/image';

import { usePromoModel } from '../../../../contexts/PromoModelProvider';
import { usePromoPresentation } from '../../../../contexts/PromoPresentationProvider';
import { BogoSecondaryMessaging } from './subcomponents/BogoSecondaryMessaging/BogoSecondaryMessaging';
import { TierSecondaryMessaging } from './subcomponents/TierSecondaryMessaging/TierSecondaryMessaging';
import { formatPrice } from '../../../../utils/promo-presentation-utils';
import { PromoViewDetails } from '../../../promo-view-details/PromoViewDetails';
import './MessagingContainer.scss';

export const MessagingContainer = ({ isInPromoDrawer }) => {
  const {
    isBogo,
    isMsb,
    isBmsm,
    subExperience,
    rewardPercent,
    minPurchaseQuantity,
    minPurchaseAmount,
    rewardAmountPerOrder,
    rewardAmountPerItem,
    hasProducts,
    isAnchorFulfillable
  } = usePromoModel();

  const { isBatchLoading, promoMsgContainerRef } = usePromoPresentation();
  const showBogoMessaging = hasProducts && isBogo;
  const showTierMessaging = hasProducts && (isMsb || isBmsm);
  const showSoldOutMessaging = isBogo
    ? !hasProducts
    : !hasProducts && !isAnchorFulfillable;

  const messagingContainerClasses = classNames('promo-messaging-container',
    {
      'promo-messaging-container--slim': !isInPromoDrawer && isBogo,
      'promo-messaging-container--in-drawer': isInPromoDrawer,
    });

  const secondaryMessagingClasses = classNames({
    'promo-messaging-container-sold-out-message': showSoldOutMessaging,
    'promo-messaging-container-secondary-message': !showSoldOutMessaging,
  });

  const logoClasses = classNames('promo-messaging-container__logo', {
    'promo-messaging-container__logo--drawer': isInPromoDrawer,
  });

  const msbMessages = {
    MSB1: <>Buy {minPurchaseQuantity} or More, Get&nbsp;{rewardPercent}%&nbsp;Off</>,
    MSB2: <>Buy {minPurchaseQuantity} or More, Save&nbsp;{formatPrice(rewardAmountPerOrder)}</>,
    MSB3: <>Buy {minPurchaseQuantity} or More, Save&nbsp;{formatPrice(rewardAmountPerItem)}&nbsp;On&nbsp;Each</>,
    MSB4: <>Spend {formatPrice(minPurchaseAmount)}, Get&nbsp;{rewardPercent}%&nbsp;Off</>,
    MSB5: <>Spend {formatPrice(minPurchaseAmount)}, Get&nbsp;{formatPrice(rewardAmountPerOrder)}&nbsp;Off</>,
    MSB6: <>Spend {formatPrice(minPurchaseAmount)}, Save&nbsp;{formatPrice(rewardAmountPerItem)}&nbsp;On&nbsp;Each</>
  };

  return (
    <>
      {isBmsm && <Image alt="" asset="orange-flame.svg" className={logoClasses} />}
      <div
        className={messagingContainerClasses}
        data-testid="promo-messaging-container"
        ref={promoMsgContainerRef}
      >
        <span>
          {isBogo && <>Free Gift with Purchase</>}
          {isMsb && msbMessages[subExperience]}
          {isBmsm && (
            <>
              Buy More, Save&nbsp;More
              {(!isBatchLoading && isAnchorFulfillable && !hasProducts) && (<PromoViewDetails />)}
            </>
          )}
        </span>
        <LoadingPlaceholder
          type="textRow"
          ready={!isBatchLoading}
          style={{ marginTop: '2px', width: '60%' }}
          color="#eeeeee"
          showLoadingAnimation
        >
          <div className={secondaryMessagingClasses} data-testid="promo-secondary-messaging">
            {showSoldOutMessaging && <>This Deal Has Sold&nbsp;Out</>}
            {showBogoMessaging && <BogoSecondaryMessaging isInPromoDrawer={isInPromoDrawer} />}
            {showTierMessaging && <TierSecondaryMessaging isInPromoDrawer={isInPromoDrawer} />}
          </div>
        </LoadingPlaceholder>
      </div>
    </>
  );
};

MessagingContainer.displayName = 'MessagingContainer';

MessagingContainer.propTypes = {
  isInPromoDrawer: bool,
};

MessagingContainer.defaultProps = {
  isInPromoDrawer: false
};
